import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trackOrder, getOrderById } from '../services/orderService';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import HomeMobileNavbar from '../components/HomeMobileNavbar';
import { showErrorToast } from '../utils/errorHandler';

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchOrderAndTracking = useCallback(async () => {
    try {
      setLoading(true);
      const orderData = await getOrderById(orderId);
      setOrder(orderData);

      if (orderData.delhivery?.waybill) {
        await trackOrder(orderId);
      }
    } catch (error) {
      console.error('Failed to fetch order tracking:', error);
      showErrorToast(error, 'Failed to load tracking information');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderAndTracking();
  }, [fetchOrderAndTracking]);

  const getStageStatus = (stage) => {
    if (!order) return 'pending';
    const currentStageIndex = getStageIndex(order.orderStage);
    const checkStageIndex = getStageIndex(stage);
    if (checkStageIndex < currentStageIndex) return 'completed';
    if (checkStageIndex === currentStageIndex) return 'current';
    return 'pending';
  };

  const getStageIndex = (stage) => {
    // Return flow
    const returnStages = ['return_requested', 'return_approved', 'return_processing', 'return_received_to_store', 'refund_initiated'];
    if (returnStages.includes(stage) || returnStages.includes(order?.orderStage)) {
      return returnStages.indexOf(stage);
    }
    // Normal flow
    const normalStages = ['ordered', 'being_made', 'shipped', 'delivered'];
    return normalStages.indexOf(stage);
  };

  const getTimelineStages = () => {
    if (!order) return [];
    const returnStages = ['return_requested', 'return_approved', 'return_processing', 'return_received_to_store', 'refund_initiated'];
    const isReturnFlow = returnStages.includes(order.orderStage);

    if (isReturnFlow) {
      return [
        { key: 'return_requested', label: 'Return Requested' },
        { key: 'return_approved', label: 'Return Approved' },
        { key: 'return_processing', label: 'Return Processing' },
        { key: 'return_received_to_store', label: 'Return Received' },
        { key: 'refund_initiated', label: 'Refund Initiated' }
      ];
    } else if (order.orderStage === 'cancelled') {
      return [{ key: 'cancelled', label: 'Order Cancelled' }];
    } else {
      return [
        { key: 'ordered', label: 'Ordered' },
        { key: 'being_made', label: 'Being Made' },
        { key: 'shipped', label: 'Shipped' },
        { key: 'delivered', label: 'Delivered' }
      ];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Pending';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeliveryProgress = () => {
    if (!order?.delhivery?.scans || order.delhivery.scans.length === 0) {
      return {
        stages: [
          { name: 'Order Placed', status: 'completed', icon: '📋' },
          { name: 'Picked Up', status: 'pending', icon: '📦' },
          { name: 'In Transit', status: 'pending', icon: '🚛' },
          { name: 'Out for Delivery', status: 'pending', icon: '🚚' },
          { name: 'Delivered', status: 'pending', icon: '✅' },
        ],
        currentStage: 'Order Placed'
      };
    }

    const scans = order.delhivery.scans;
    const stages = [
      { name: 'Order Placed', status: 'completed', icon: '📋' },
      { name: 'Picked Up', status: 'pending', icon: '📦' },
      { name: 'In Transit', status: 'pending', icon: '🚛' },
      { name: 'Out for Delivery', status: 'pending', icon: '🚚' },
      { name: 'Delivered', status: 'pending', icon: '✅' },
    ];

    let currentStage = 'Order Placed';

    scans.forEach(scan => {
      const detail = scan.ScanDetail?.toLowerCase() || '';
      
      if (detail.includes('delivered')) {
        stages[4].status = 'completed';
        stages[3].status = 'completed';
        stages[2].status = 'completed';
        stages[1].status = 'completed';
        currentStage = 'Delivered';
      } else if (detail.includes('out for delivery') || detail.includes('loaded for delivery')) {
        stages[3].status = 'completed';
        stages[2].status = 'completed';
        stages[1].status = 'completed';
        if (currentStage !== 'Delivered') currentStage = 'Out for Delivery';
      } else if (detail.includes('in transit') || detail.includes('dispatched')) {
        stages[2].status = 'completed';
        stages[1].status = 'completed';
        if (currentStage !== 'Out for Delivery' && currentStage !== 'Delivered') {
          currentStage = 'In Transit';
        }
      } else if (detail.includes('picked up') || detail.includes('pickup')) {
        stages[1].status = 'completed';
        if (currentStage === 'Order Placed') currentStage = 'Picked Up';
      }
    });

    // Set current active stage
    const lastCompletedIndex = stages.findIndex((s, i) => i > 0 && s.status === 'completed' && stages[i + 1]?.status === 'pending');
    if (lastCompletedIndex !== -1 && lastCompletedIndex < stages.length - 1) {
      stages[lastCompletedIndex + 1].status = 'current';
    }

    return { stages, currentStage };
  };

  const deliveryProgress = order ? getDeliveryProgress() : null;

  if (loading) {
    return (
      <div>
        {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Order not found</h2>
            <button
              onClick={() => navigate('/orders')}
              className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}
      <div className="max-w-7xl mx-auto mt-20 mb-10 px-5 font-bdogrotesk">
        <div className="text-center mb-12">
          <button onClick={() => navigate('/orders')} className="text-base text-gray-600 hover:text-black mb-4">
            ← Back to Orders
          </button>
          <h1 className="text-4xl font-semibold mb-2">Track Your Order</h1>
          <p className="text-base text-gray-600">Order ID: #{order._id.slice(-8)}</p>
        </div>

        {/* Order Timeline */}
        <div className="bg-white border border-gray-200 rounded-xl p-10 mb-8">
          <div className="flex items-center justify-between relative">
            {/* Desktop horizontal timeline */}
            <div className="hidden md:flex items-center justify-between w-full relative">
              {/* Progress line */}
              <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200 z-0"></div>

              {getTimelineStages().map((stage, index) => (
                <div key={stage.key} className={`flex-1 flex flex-col items-center relative z-10`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold mb-3 ${
                    getStageStatus(stage.key) === 'completed' ? 'bg-black text-white' :
                    getStageStatus(stage.key) === 'current' ? 'bg-green-500 text-white animate-pulse' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {getStageStatus(stage.key) === 'completed' ? '✓' : index + 1}
                  </div>
                  <div className="text-center">
                    <h3 className="text-base font-semibold mb-2">{stage.label}</h3>
                    <p className="text-sm text-gray-600">
                      {stage.key === 'ordered' && order.createdAt
                        ? formatDate(order.createdAt)
                        : stage.key === 'delivered' && order.deliveredAt
                        ? formatDate(order.deliveredAt)
                        : order.stageHistory?.find((h) => h.stage === stage.key)
                        ? formatDate(order.stageHistory.find((h) => h.stage === stage.key).timestamp)
                        : 'Pending'}
                    </p>
                    {stage.key === 'ordered' && order.createdAt && (
                      <p className="text-xs text-gray-400">{formatTime(order.createdAt)}</p>
                    )}
                    {stage.key === 'shipped' && order.delhivery?.waybill && (
                      <p className="text-xs font-mono text-black mt-2">
                        Tracking: {order.delhivery.waybill}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile vertical timeline */}
            <div className="flex md:hidden flex-col w-full space-y-6">
              {getTimelineStages().map((stage, index) => (
                <div key={stage.key} className="flex items-start">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold mr-4 flex-shrink-0 ${
                    getStageStatus(stage.key) === 'completed' ? 'bg-black text-white' :
                    getStageStatus(stage.key) === 'current' ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {getStageStatus(stage.key) === 'completed' ? '✓' : index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold mb-1">{stage.label}</h3>
                    <p className="text-sm text-gray-600">
                      {stage.key === 'ordered' && order.createdAt
                        ? formatDate(order.createdAt)
                        : stage.key === 'delivered' && order.deliveredAt
                        ? formatDate(order.deliveredAt)
                        : order.stageHistory?.find((h) => h.stage === stage.key)
                        ? formatDate(order.stageHistory.find((h) => h.stage === stage.key).timestamp)
                        : 'Pending'}
                    </p>
                    {stage.key === 'ordered' && order.createdAt && (
                      <p className="text-xs text-gray-400">{formatTime(order.createdAt)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Delivery Progress Stages */}
        {order?.delhivery?.waybill && deliveryProgress && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-6">Delivery Progress</h2>
            
            {/* Current Status Badge */}
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full">
                <span className="text-2xl">{deliveryProgress.stages.find(s => s.name === deliveryProgress.currentStage)?.icon}</span>
                <span className="font-semibold text-blue-900 text-lg">Current: {deliveryProgress.currentStage}</span>
              </div>
            </div>

            {/* Progress Bar - Desktop */}
            <div className="hidden md:block">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-500"
                    style={{ 
                      width: `${(deliveryProgress.stages.filter(s => s.status === 'completed').length - 1) / (deliveryProgress.stages.length - 1) * 100}%` 
                    }}
                  ></div>
                </div>
                
                {/* Stage Markers */}
                <div className="relative flex justify-between">
                  {deliveryProgress.stages.map((stage, index) => (
                    <div key={index} className="flex flex-col items-center" style={{ width: '20%' }}>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl mb-3 transition-all ${
                        stage.status === 'completed' ? 'bg-green-500 text-white shadow-lg scale-110' :
                        stage.status === 'current' ? 'bg-blue-500 text-white animate-pulse shadow-lg' :
                        'bg-gray-200 text-gray-400'
                      }`}>
                        {stage.icon}
                      </div>
                      <span className={`text-sm font-medium text-center ${
                        stage.status === 'completed' ? 'text-green-700' :
                        stage.status === 'current' ? 'text-blue-700' :
                        'text-gray-500'
                      }`}>
                        {stage.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Progress Bar - Mobile */}
            <div className="md:hidden space-y-4">
              {deliveryProgress.stages.map((stage, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl flex-shrink-0 ${
                    stage.status === 'completed' ? 'bg-green-500 text-white' :
                    stage.status === 'current' ? 'bg-blue-500 text-white animate-pulse' :
                    'bg-gray-200 text-gray-400'
                  }`}>
                    {stage.icon}
                  </div>
                  <div className="flex-1">
                    <span className={`font-medium ${
                      stage.status === 'completed' ? 'text-green-700' :
                      stage.status === 'current' ? 'text-blue-700' :
                      'text-gray-500'
                    }`}>
                      {stage.name}
                    </span>
                    {stage.status === 'current' && (
                      <p className="text-xs text-blue-600 mt-1">In Progress</p>
                    )}
                    {stage.status === 'completed' && (
                      <p className="text-xs text-green-600 mt-1">✓ Completed</p>
                    )}
                  </div>
                  {index < deliveryProgress.stages.length - 1 && (
                    <div className={`w-0.5 h-8 ml-6 ${
                      stage.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Scan History */}
        {order?.delhivery?.scans && order.delhivery.scans.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-6">Detailed Tracking History</h2>
            <div className="space-y-3">
              {[...order.delhivery.scans].reverse().map((scan, index) => (
                <div key={index} className={`p-4 rounded-lg border ${
                  index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  {index === 0 && (
                    <span className="inline-block px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded mb-2">
                      Latest Update
                    </span>
                  )}
                  <div className="font-semibold text-sm mb-2">
                    {formatDate(scan.ScanDateTime)}
                    <span className="ml-3 text-gray-600 font-normal">
                      {formatTime(scan.ScanDateTime)}
                    </span>
                  </div>
                  <div className="text-sm mb-1 font-medium">{scan.ScanDetail}</div>
                  <div className="text-xs text-gray-600 flex items-center gap-1">
                    <span>📍</span>
                    {scan.ScannedLocation}
                  </div>
                  {scan.Instructions && (
                    <div className="text-xs text-gray-700 mt-2 bg-white p-2 rounded">
                      ℹ️ {scan.Instructions}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-semibold mb-6">Order Items</h2>
          <div className="space-y-3">
            {order.items.map((item) => (
              <div key={item._id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                {item.image && (
                  <img src={item.image} alt={item.productName} className="w-20 h-20 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <h4 className="text-base font-semibold mb-2">{item.productName}</h4>
                  <p className="text-sm text-gray-600 mb-1">
                    {item.print && `${item.print} - `}
                    Size: {item.size}
                  </p>
                  <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                  <p className="text-base font-semibold mt-2">₹ {item.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Shipping Address */}
        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <h2 className="text-2xl font-semibold mb-4">Shipping Address</h2>
          <p className="text-sm text-gray-700 mb-1">{order.shippingAddress.name}</p>
          <p className="text-sm text-gray-700 mb-1">
            {order.shippingAddress.line1}, {order.shippingAddress.line2}
          </p>
          <p className="text-sm text-gray-700 mb-1">
            {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
            {order.shippingAddress.postalCode}
          </p>
          <p className="text-sm text-gray-700">Phone: {order.shippingAddress.phone}</p>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
